/**
 * Messaging Configuration
 *
 * This file contains all messaging variations for the app.
 * To switch messaging, change the ACTIVE_VERSION constant below.
 *
 * Each version targets a specific audience and psychological hook.
 */

export const messagingVariations = {
  // VERSION 1: "Finally" - Relief from Embarrassment
  version1: {
    id: "guilty-returner-finally",
    name: "Finally (Guilty Returner)",
    targetAudience: "Lapsed Christians 28-45, feeling embarrassed",

    headline: "Finally Understand the Bible",
    subheadline: "Ask anything. No judgment.",

    inputPlaceholders: [
      "What Bible question are you embarrassed to ask?",
      "Who was David?",
      "What's a parable?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Anything",
        description: "Seriously. Even 'basic' questions.",
      },
      card2: {
        icon: "🗺️",
        title: "See It Visually",
        description: "How stories and people connect",
      },
      card3: {
        icon: "🧭",
        title: "Your Own Path",
        description: "Learn what matters to you, not a preset plan",
      },
    },
  },

  // VERSION 2: "Should Have" - Guilt-Driven
  version2: {
    id: "should-have-learned",
    name: "Should Have (Guilt-Driven)",
    targetAudience: "Adults who feel they should know the Bible",

    headline: "The Bible You Should Have Learned",
    subheadline: "Start now. Start anywhere.",

    inputPlaceholders: [
      "What Bible story confuses you?",
      "Where should I even start?",
      "Who was Moses?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "No Dumb Questions",
        description: "If you're wondering, it matters",
      },
      card2: {
        icon: "🔗",
        title: "Make Connections",
        description: "See how it all fits together",
      },
      card3: {
        icon: "📖",
        title: "Build Knowledge",
        description: "One question at a time",
      },
    },
  },

  // VERSION 3: "Ask Discover Connect" - Action-Based
  version3: {
    id: "ask-discover-connect",
    name: "Ask Discover Connect (Original)",
    targetAudience: "All audiences, action-oriented",

    headline: "Explore the Bible",
    subheadline: "Ask. Discover. Connect.",

    inputPlaceholders: [
      "Ask any question about the Bible...",
      "Who was David?",
      "What's the Exodus about?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Anything",
        description: "No dumb questions.",
      },
      card2: {
        icon: "🗺️",
        title: "See Connections",
        description: "Visual map shows how people, places, and stories connect",
      },
      card3: {
        icon: "🧭",
        title: "Go Deeper",
        description: "Click anything to explore further. Build your own path.",
      },
    },
  },

  // VERSION 4: "Curiosity-Led" - Wonder & Exploration
  version4: {
    id: "curiosity-wonder",
    name: "Curiosity (Wonder-Driven)",
    targetAudience: "Intellectually curious seekers",

    headline: "Curious About the Bible?",
    subheadline: "Discover stories. See connections.",

    inputPlaceholders: [
      "What are you curious about?",
      "Who was Jonah?",
      "What's the story of creation?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Questions",
        description: "Curiosity is the first step",
      },
      card2: {
        icon: "🔍",
        title: "Discover Stories",
        description: "Fascinating people and events",
      },
      card3: {
        icon: "🗺️",
        title: "See How It Connects",
        description: "Visual map of relationships",
      },
    },
  },

  // VERSION 5: "Visual Learning" - ADHD/Visual Learners
  version5: {
    id: "visual-learning",
    name: "Visual Learning (ADHD-Friendly)",
    targetAudience: "Visual learners, ADHD, struggle with traditional reading",

    headline: "Explore the Bible Visually",
    subheadline: "For people who learn by exploring.",

    inputPlaceholders: [
      "Ask anything...",
      "Who was David?",
      "What's a miracle Jesus did?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Questions",
        description: "Start with what you're curious about",
      },
      card2: {
        icon: "🗺️",
        title: "See the Map",
        description: "Visual structure, not linear text",
      },
      card3: {
        icon: "🔗",
        title: "Follow Connections",
        description: "Learn the way your brain works",
      },
    },
  },

  // VERSION 6: "No Pressure" - Removes Intimidation
  version6: {
    id: "no-pressure",
    name: "No Pressure (Beginner-Friendly)",
    targetAudience: "Intimidated beginners, first-timers",

    headline: "Understand the Bible",
    subheadline: "No pressure. Just understanding.",

    inputPlaceholders: [
      "What confuses you about the Bible?",
      "Who was Abraham?",
      "What's the Ten Commandments?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Freely",
        description: "Every expert was once a beginner",
      },
      card2: {
        icon: "🗺️",
        title: "Learn Visually",
        description: "See how it connects",
      },
      card3: {
        icon: "🌱",
        title: "Grow Spiritually",
        description: "At your own pace",
      },
    },
  },

  // VERSION 7: "Cultural Literacy" - Skeptical Seekers
  version7: {
    id: "cultural-literacy",
    name: "Cultural Literacy (Skeptical Seekers)",
    targetAudience: "Agnostic, atheist, culturally curious",

    headline: "Understand the Bible",
    subheadline: "The book that shaped humanity.",

    inputPlaceholders: [
      "What Bible reference went over your head?",
      "What's the David and Goliath story?",
      "Who was Solomon?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Everything",
        description: "Cultural references, stories, characters",
      },
      card2: {
        icon: "🗺️",
        title: "See the Map",
        description: "How it all connects visually",
      },
      card3: {
        icon: "📚",
        title: "Build Literacy",
        description: "One discovery at a time",
      },
    },
  },

  // VERSION 8: "Life Questions" - Crisis-Driven
  version8: {
    id: "life-questions",
    name: "Life Questions (Crisis-Driven)",
    targetAudience: "People facing life challenges, seeking wisdom",

    headline: "Find Wisdom for Life's Questions",
    subheadline: "Explore timeless answers.",

    inputPlaceholders: [
      "What's on your heart today?",
      "Find peace",
      "Find clarity",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Anything",
        description: "Life, faith, meaning, purpose",
      },
      card2: {
        icon: "🗺️",
        title: "Discover Wisdom",
        description: "Stories that speak to your situation",
      },
      card3: {
        icon: "🧭",
        title: "Find Your Path",
        description: "Guidance for your journey",
      },
    },
  },

  // VERSION 9: "Journey Builder" - Growth-Minded
  version9: {
    id: "journey-builder",
    name: "Journey Builder (Growth-Minded)",
    targetAudience: "Self-improvement focused, lifelong learners",

    headline: "Your Bible Journey",
    subheadline: "Starts with one question.",

    inputPlaceholders: [
      "What's your first question?",
      "Begin your journey...",
      "Who was Peter?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask & Learn",
        description: "Every journey begins with curiosity",
      },
      card2: {
        icon: "🗺️",
        title: "Build Your Map",
        description: "See your knowledge grow visually",
      },
      card3: {
        icon: "📈",
        title: "Track Progress",
        description: "Watch your understanding deepen",
      },
    },
  },

  // VERSION 10: "Remember" - Nostalgic Returners
  version10: {
    id: "remember-refresh",
    name: "Remember (Nostalgic Returners)",
    targetAudience: "People who learned as kids, want to refresh",

    headline: "Remember the Bible",
    subheadline: "Refresh what you once knew.",

    inputPlaceholders: [
      "What Bible story do you vaguely remember?",
      "Who was that guy with the coat of many colors?",
      "Refresh my memory...",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Questions",
        description: "Like you would a patient friend",
      },
      card2: {
        icon: "🗺️",
        title: "See Connections",
        description: "Refresh your memory visually",
      },
      card3: {
        icon: "🎯",
        title: "Go Deeper",
        description: "When you're ready",
      },
    },
  },

  // VERSION 11: "Interactive Learning" - Engagement-Focused
  version11: {
    id: "interactive-learning",
    name: "Interactive Learning (Engagement-Focused)",
    targetAudience: "Active learners who prefer hands-on exploration",

    headline: "Explore the Bible Interactively",
    subheadline: "For people who learn by exploring.",

    inputPlaceholders: [
      "Ask anything...",
      "Who was David?",
      "What's a miracle Jesus did?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Questions",
        description: "Start with what you're curious about",
      },
      card2: {
        icon: "🔗",
        title: "Explore Connections",
        description: "Interactive flow, not static reading",
      },
      card3: {
        icon: "🎯",
        title: "Follow Your Path",
        description: "Click, explore, discover",
      },
    },
  },

  // VERSION 12: "No Agenda" - Curiosity-Driven (Skeptics)
  version12: {
    id: "no-agenda-curiosity",
    name: "No Agenda (Curiosity-Driven)",
    targetAudience: "Skeptics, non-religious seekers",

    headline: "Explore the Bible",
    subheadline: "No agenda. Just understanding.",

    inputPlaceholders: [
      "What are you curious about?",
      "What's the Garden of Eden story?",
      "Who was Abraham?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Freely",
        description: "No conversion. No preaching.",
      },
      card2: {
        icon: "🔍",
        title: "Explore Stories",
        description: "Historical context and meaning",
      },
      card3: {
        icon: "🗺️",
        title: "See Connections",
        description: "How it all fits together",
      },
    },
  },

  // VERSION 13: "Get the References" - Practical Benefit
  version13: {
    id: "get-references",
    name: "Get the References (Practical)",
    targetAudience: "People tired of missing cultural references",

    headline: "Get the Bible References",
    subheadline: "That everyone keeps making.",

    inputPlaceholders: [
      "What Bible reference went over your head?",
      "What does 'Good Samaritan' mean?",
      "What's 'the writing on the wall'?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask About References",
        description: "Movies, books, art, sayings",
      },
      card2: {
        icon: "📖",
        title: "Get the Story",
        description: "Where it comes from and what it means",
      },
      card3: {
        icon: "🔗",
        title: "See More",
        description: "Related stories and references",
      },
    },
  },

  // VERSION 14: "Decode" - Cultural Hook
  version14: {
    id: "decode-culture",
    name: "Decode (Cultural Hook)",
    targetAudience: "Art, literature, history enthusiasts",

    headline: "Decode the Bible",
    subheadline: "Stories that shaped culture.",

    inputPlaceholders: [
      "What Bible story is this artwork about?",
      "What's the story of David and Goliath?",
      "Who was Mary Magdalene?",
    ],

    journeyCards: {
      card1: {
        icon: "🎨",
        title: "Understand Art",
        description: "Biblical stories in paintings and sculpture",
      },
      card2: {
        icon: "📚",
        title: "Literary References",
        description: "Shakespeare to modern novels",
      },
      card3: {
        icon: "🏛️",
        title: "Historical Context",
        description: "How these stories shaped culture",
      },
    },
  },

  // VERSION 15: "Bible Explained" - Simple & Direct
  version15: {
    id: "bible-explained",
    name: "Bible Explained (Simple & Direct)",
    targetAudience: "Culturally curious, straightforward seekers",

    headline: "The Bible, Explained",
    subheadline: "For curious people.",

    inputPlaceholders: [
      "Explain a Bible story...",
      "What's the Tower of Babel?",
      "Who was Noah?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Ask Questions",
        description: "Simple explanations, no jargon",
      },
      card2: {
        icon: "📖",
        title: "Get Context",
        description: "Historical and cultural background",
      },
      card3: {
        icon: "🔗",
        title: "Explore More",
        description: "Related stories and themes",
      },
    },
  },

  // VERSION 16: "No Beliefs Required" - Knowledge-Focused
  version16: {
    id: "no-beliefs-required",
    name: "No Beliefs Required (Knowledge-Focused)",
    targetAudience: "Agnostics wanting knowledge without pressure",

    headline: "Understand Bible Stories",
    subheadline: "No beliefs required. Just curiosity.",

    inputPlaceholders: [
      "What are you curious about?",
      "What's the Prodigal Son story?",
      "Who was King Solomon?",
    ],

    journeyCards: {
      card1: {
        icon: "💬",
        title: "Learn Without Pressure",
        description: "Knowledge, not conversion",
      },
      card2: {
        icon: "📖",
        title: "Understand Stories",
        description: "Context, meaning, and impact",
      },
      card3: {
        icon: "🗺️",
        title: "Explore Freely",
        description: "Follow your curiosity",
      },
    },
  },

  // VERSION 17: "Questions into Discovery" - Process-Focused
  version17: {
    id: "questions-discovery",
    name: "Questions into Discovery (Process-Focused)",
    targetAudience: "Active learners, faith-curious seekers",

    headline: "Turn Questions into Discovery",
    subheadline: "Ask. Explore. Discover.",

    inputPlaceholders: [
      "What question will you start with?",
      "Who was Paul?",
      "What's the Sermon on the Mount?",
    ],

    journeyCards: {
      card1: {
        icon: "❓",
        title: "Ask Questions",
        description: "Every discovery starts with curiosity",
      },
      card2: {
        icon: "🔍",
        title: "Explore Answers",
        description: "Deep dive into God's Word",
      },
      card3: {
        icon: "✨",
        title: "Discover Truth",
        description: "Build your understanding step by step",
      },
    },
  },

  // VERSION 18: "Faith Growth" - Personal Journey
  version18: {
    id: "faith-growth",
    name: "Faith Growth (Personal Journey)",
    targetAudience: "Growing Christians, personalization seekers",

    headline: "See Your Faith Grow",
    subheadline: "Your Bible, Your Way.",

    inputPlaceholders: [
      "What would help your faith grow today?",
      "How can I pray better?",
      "What does faith mean?",
    ],

    journeyCards: {
      card1: {
        icon: "🌱",
        title: "Start Growing",
        description: "Build your faith foundation",
      },
      card2: {
        icon: "📖",
        title: "Your Personalized Path",
        description: "Learn what matters to you",
      },
      card3: {
        icon: "📈",
        title: "Track Your Growth",
        description: "See your spiritual journey unfold",
      },
    },
  },

  // VERSION 19: "Hope & Wisdom" - Emotional Need
  version19: {
    id: "hope-wisdom",
    name: "Hope & Wisdom (Emotional Need)",
    targetAudience: "People seeking hope, guidance, practical wisdom",

    headline: "Find Hope in Every Chapter",
    subheadline: "Wisdom for Today's Questions.",

    inputPlaceholders: [
      "What do you need hope for today?",
      "How do I find peace?",
      "Where is God in my struggles?",
    ],

    journeyCards: {
      card1: {
        icon: "💫",
        title: "Find Hope",
        description: "Stories of encouragement and strength",
      },
      card2: {
        icon: "🧭",
        title: "Get Wisdom",
        description: "Biblical guidance for real life",
      },
      card3: {
        icon: "🙏",
        title: "Apply Today",
        description: "Practical steps for your situation",
      },
    },
  },

  // VERSION 20: "Reimagined Learning" - Modern & Innovative
  version20: {
    id: "reimagined-learning",
    name: "Reimagined Learning (Modern & Innovative)",
    targetAudience: "Tech-savvy, modern learners wanting fresh approach",

    headline: "Your Faith. Visualized.",
    subheadline: "Bible Learning Reimagined.",

    inputPlaceholders: [
      "Start your journey...",
      "Who was Jesus?",
      "What are the Beatitudes?",
    ],

    journeyCards: {
      card1: {
        icon: "🎯",
        title: "Modern Learning",
        description: "Not your traditional Bible study",
      },
      card2: {
        icon: "🗺️",
        title: "Visual Journey",
        description: "See your faith path mapped out",
      },
      card3: {
        icon: "⚡",
        title: "Interactive Experience",
        description: "Click, explore, connect",
      },
    },
  },

  // VERSION 21: "Fresh Perspective" - Transformative
  version21: {
    id: "fresh-perspective",
    name: "Fresh Perspective (Transformative)",
    targetAudience: "People wanting new approach, transformation seekers",

    headline: "Explore the Bible Like Never Before",
    subheadline: "See the Bible in a New Light.",

    inputPlaceholders: [
      "What do you want to explore?",
      "Tell me about grace",
      "What's the Gospel?",
    ],

    journeyCards: {
      card1: {
        icon: "💡",
        title: "New Perspective",
        description: "Fresh insights into timeless truth",
      },
      card2: {
        icon: "🔗",
        title: "See Connections",
        description: "How it all fits together",
      },
      card3: {
        icon: "🌟",
        title: "Transform Understanding",
        description: "The Bible becomes clear",
      },
    },
  },

  // VERSION 22: "Made Clear" - Clarity-Focused
  version22: {
    id: "made-clear",
    name: "Made Clear (Clarity-Focused)",
    targetAudience: "People wanting simple, clear understanding",

    headline: "The Bible Made Clear",
    subheadline: "One question at a time.",

    inputPlaceholders: [
      "What's on your heart today?",
      "Who was David?",
      "What's the Gospel?",
    ],

    journeyCards: {
      card1: {
        icon: "💡",
        title: "Get Clarity",
        description: "Simple explanations that make sense",
      },
      card2: {
        icon: "🗺️",
        title: "See Connections",
        description: "How it all fits together visually",
      },
      card3: {
        icon: "✨",
        title: "Build Understanding",
        description: "One clear answer at a time",
      },
    },
  },
};

/**
 * ACTIVE VERSION
 *
 * Change this to switch messaging across the entire app.
 * Options: version1, version2, version3, version4, version5, version6,
 *          version7, version8, version9, version10, version11, version12,
 *          version13, version14, version15, version16, version17, version18,
 *          version19, version20, version21, version22
 *
 * Recommended: version1 (Guilty Returner - highest converting audience)
 */
// export const ACTIVE_VERSION = "version3";
// export const ACTIVE_VERSION = "version5";
// export const ACTIVE_VERSION = "version6";
// export const ACTIVE_VERSION = "version8";
// export const ACTIVE_VERSION = "version9";
// export const ACTIVE_VERSION = "version11";
// export const ACTIVE_VERSION = "version12";
// export const ACTIVE_VERSION = "version14";
// export const ACTIVE_VERSION = "version17";
// export const ACTIVE_VERSION = "version18";
// export const ACTIVE_VERSION = "version19";
// export const ACTIVE_VERSION = "version20";
export const ACTIVE_VERSION = "version21";

/**
 * Get list of all available versions (for future admin panel)
 */
export const getAvailableVersions = () => {
  return Object.keys(messagingVariations).map((key) => ({
    key,
    name: messagingVariations[key].name,
    targetAudience: messagingVariations[key].targetAudience,
  }));
};
